import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";

import * as api from "../api";

/**
 * Mic button for the chat composer.
 * Records via MediaRecorder -> POST /api/cases/{id}/transcribe -> Whisper.
 * Calls onTranscribed(text) so the parent can drop it into the editable input.
 * Audio is transient (never persisted server-side).
 */
export default function VoiceRecorder({ caseId, onTranscribed, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => () => {
    // cleanup on unmount
    if (tickRef.current) clearInterval(tickRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    const cands = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    return cands.find((m) => MediaRecorder.isTypeSupported(m)) || "";
  }

  async function start() {
    if (state !== "idle" || disabled) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const opts = mimeType ? { mimeType } : undefined;
      const rec = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = handleStop;
      recorderRef.current = rec;
      rec.start();
      setState("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= 60) { // 60s hard cap
            stop();
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error(
        e?.name === "NotAllowedError"
          ? "Please allow microphone access to use voice input"
          : "Could not start recording"
      );
    }
  }

  function stop() {
    if (state !== "recording") return;
    const rec = recorderRef.current;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (rec && rec.state === "recording") {
      rec.stop();
    }
  }

  async function handleStop() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (!chunks.length) {
      setState("idle");
      return;
    }
    const blob = new Blob(chunks, {
      type: recorderRef.current?.mimeType || "audio/webm",
    });
    if (blob.size < 1200) {
      // < ~150ms — almost certainly noise
      toast.error("Recording too short — try again");
      setState("idle");
      return;
    }
    setState("transcribing");
    try {
      const { text } = await api.transcribeAudio(caseId, blob);
      if (!text || !text.trim()) {
        toast.error("Couldn't hear anything — try again");
      } else {
        onTranscribed(text.trim());
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed");
    } finally {
      setState("idle");
      setSeconds(0);
    }
  }

  const recording = state === "recording";
  const transcribing = state === "transcribing";

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || transcribing}
      title={recording ? "Stop recording" : "Speak your message"}
      aria-label={recording ? "Stop recording" : "Start voice input"}
      data-testid={
        recording ? "chat-mic-stop-btn" :
        transcribing ? "chat-mic-transcribing-btn" :
        "chat-mic-btn"
      }
      className={[
        "shrink-0 inline-flex items-center justify-center rounded-lg border text-sm font-medium transition-colors",
        "h-[46px]",
        recording
          ? "px-3 bg-red-600 text-white border-red-600 hover:bg-red-700 animate-pulse"
          : transcribing
            ? "px-3 bg-slate-100 text-slate-500 border-slate-200 cursor-wait"
            : "px-3 bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
        (disabled && !recording && !transcribing) ? "opacity-50 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {transcribing ? (
        <Loader2 size={16} className="animate-spin" />
      ) : recording ? (
        <span className="inline-flex items-center gap-1.5">
          <Square size={12} fill="currentColor" />
          <span className="font-mono tabular-nums text-xs" data-testid="chat-mic-timer">
            {String(Math.floor(seconds / 60)).padStart(1, "0")}:
            {String(seconds % 60).padStart(2, "0")}
          </span>
        </span>
      ) : (
        <Mic size={16} />
      )}
    </button>
  );
}
